import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "cargo_cart_v2";

const lineKey = (productId, variationId) =>
  `${productId}::${variationId || ""}`;

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  /**
   * Add a product (optionally a variation) to the cart.
   * line: {
   *   product_id, variation_id?, name, variation_name?, sku, image, slug,
   *   price (effective), regular_price, sale_price, vat_percentage,
   *   stripe_price_id, stock_status, stock_qty, is_service
   * }
   */
  const addItem = (line, qty = 1) => {
    if (!line?.product_id) return;
    const key = lineKey(line.product_id, line.variation_id);
    setItems((prev) => {
      const existing = prev.find((i) => lineKey(i.product_id, i.variation_id) === key);
      if (existing) {
        return prev.map((i) =>
          lineKey(i.product_id, i.variation_id) === key
            ? { ...i, quantity: Math.max(1, i.quantity + qty) }
            : i
        );
      }
      return [...prev, { ...line, quantity: Math.max(1, qty) }];
    });
  };

  const updateQty = (productId, variationId, qty) => {
    const key = lineKey(productId, variationId);
    setItems((prev) =>
      prev
        .map((i) =>
          lineKey(i.product_id, i.variation_id) === key
            ? { ...i, quantity: Math.max(1, qty) }
            : i
        )
        .filter((i) => i.quantity > 0)
    );
  };

  const removeItem = (productId, variationId) => {
    const key = lineKey(productId, variationId);
    setItems((prev) => prev.filter((i) => lineKey(i.product_id, i.variation_id) !== key));
  };

  const clear = () => setItems([]);

  const totals = useMemo(() => {
    const subtotal = items.reduce((s, i) => s + (Number(i.price) || 0) * i.quantity, 0);
    const count = items.reduce((s, i) => s + i.quantity, 0);
    // VAT bedrag — assumes prices zijn inclusief BTW (NL standaard)
    const vat = items.reduce((s, i) => {
      const pct = Number(i.vat_percentage) || 0;
      const lineTotal = (Number(i.price) || 0) * i.quantity;
      const vatPart = lineTotal - lineTotal / (1 + pct / 100);
      return s + vatPart;
    }, 0);
    return {
      subtotal: Math.round(subtotal * 100) / 100,
      vat: Math.round(vat * 100) / 100,
      count,
    };
  }, [items]);

  return (
    <CartContext.Provider
      value={{ items, addItem, updateQty, removeItem, clear, totals, lineKey }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
};
